import { createContext, FunctionComponent, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { DragPreview } from './DragPreview';
import { DraggingAutoScrollContainer } from './DraggingAutoScrollContainer';

export interface IDragDropZone {
  id: string;
  element: HTMLElement;
  isOver: boolean;
  noVerticalTicks?: boolean;
  noHorizontalTicks?: boolean;
  currentVerticalIndex?: number;
  currentHorizontalIndex?: number;
}

interface IDragAndDropContext {
  disabled?: boolean;
  verticalTicksPerDropZone?: number;
  horizontalTicksPerDropZone?: number;
}

interface IDragAndDropContextInternal {
  dropZones: IDragDropZone[];
  activeDropZone: IDragDropZone | undefined;
  registerDropZone: (dropZone: IDragDropZone) => void;
  toggleDropZone: (id: string, active: boolean) => void;
  removeDropZone: (id: string) => void;
  setActiveDropZoneCurrentDirectionalIndexes: (verticalIndex?: number, horizontalIndex?: number) => void;
}

const contextInitialInternalState: IDragAndDropContextInternal = {
  dropZones: [],
  activeDropZone: undefined,
  registerDropZone: () => null,
  toggleDropZone: () => null,
  removeDropZone: () => null,
  setActiveDropZoneCurrentDirectionalIndexes: () => null
};
const contextInitialState: IDragAndDropContext & IDragAndDropContextInternal = {
  disabled: false,
  ...contextInitialInternalState
}
const DragAndDropContext = createContext<IDragAndDropContext & IDragAndDropContextInternal>(contextInitialState);

export const useDragAndDropContext = () => useContext(DragAndDropContext);

interface IDragAndDropContextProviderProps extends IDragAndDropContext {
  children: ReactNode;
}
export const DragAndDropContextProvider : FunctionComponent<IDragAndDropContextProviderProps> = (props: IDragAndDropContextProviderProps) => {

  const {
    children,
    ...context
  } = props;

  const [state, setState] = useState<IDragAndDropContextInternal>(contextInitialInternalState);

  const activeDropZone = useMemo(() => {
    return state.dropZones.find(x => x.isOver);
  }, [state.dropZones]);

  const registerDropZone = useCallback((dropZone: IDragDropZone) => {
    setState(s => ({
      ...s,
      dropZones: [dropZone]
        .concat(s.dropZones.filter(x => x.id !== dropZone.id))
    }));
  }, []);

  const toggleDropZone = useCallback((id: string, active: boolean) => {
    setState(s => ({
      ...s,
      dropZones: s.dropZones
        .map(x => x.id !== id ? x : {
          ...x,
          isOver: active,
          currentVerticalIndex: active ? x.currentVerticalIndex : undefined,
          currentHorizontalIndex: active ? x.currentHorizontalIndex : undefined
        })
    }));
  }, []);

  const removeDropZone = useCallback((id: string) => {
    setState(s => ({
      ...s,
      dropZones: s.dropZones
        .filter(x => x.id !== id)
    }));
  }, []);

  const setActiveDropZoneCurrentDirectionalIndexes = useCallback((verticalIndex?: number, horizontalIndex?: number) => {
    setState(s => ({
      ...s,
      dropZones: s.dropZones
        .map(x => !x.isOver ? x : {
          ...x,
          currentVerticalIndex: verticalIndex,
          currentHorizontalIndex: horizontalIndex
        })
    }));
  }, []);

  return (
    <DragAndDropContext.Provider value={{
      ...context,
      ...state,
      activeDropZone,
      registerDropZone,
      removeDropZone,
      toggleDropZone,
      setActiveDropZoneCurrentDirectionalIndexes
    }}>
      <DndProvider backend={HTML5Backend}>
        <DraggingAutoScrollContainer>
          <DragPreview />
          
          {children}
        </DraggingAutoScrollContainer>
      </DndProvider>
    </DragAndDropContext.Provider>
  );
}